import { useCallback, useEffect, useState } from 'react'
import { GridApi, IDoesFilterPassParams, IRowNode } from 'ag-grid-community'
import { CustomFilterProps, useGridFilter } from 'ag-grid-react'
import {
  BulkSheetFilter,
  FilterFooter,
  FilterInput,
} from '../../../../containers/BulkSheetView/components/filter/common'
import {
  SelectFilter,
  SelectFilterArea,
  SelectFilterFormControl,
  CheckboxArea,
  FilterLabel,
} from '../../../../containers/BulkSheetView/components/filter/SelectFilter'
import { Checkbox, Typography } from '@mui/material'
import { ICON_SIZE } from '../../../../containers/BulkSheet/const'
import {
  MemberWorkloadStatusType,
  MemberWorkloadStatusRow,
} from '../../MemberWorkloadStatus'
import { getEstimatedActualTypeLabel } from '../../gridOptions/columnDefGroupRowsByMemberCol'
import { allDescendants } from '../../../../containers/BulkSheetView/lib/gridApi'

type Props = CustomFilterProps & {
  getValue: (option: any) => string
  getLabel: (option: any) => string
  valueGetter: (params: any) => any
  valueFormatter: (params: any) => string
  sortValues: (uiMeta: any, options: any, context: any) => any
  optionKey?: string
  getValuesForOptionFilter?: (context: any) => Promise<string[]>
}

const isMemberRow = (data?: MemberWorkloadStatusRow) =>
  data?.type === MemberWorkloadStatusType.MEMBER

const isTotalEstimatedActualRow = (data?: MemberWorkloadStatusRow) =>
  data?.type === MemberWorkloadStatusType.TOTAL_ESTIMATED_ACTUAL

const getChildren = (node: IRowNode): IRowNode[] =>
  node.childrenAfterGroup || allDescendants(node)

const passesTypeFilter = (
  api: GridApi,
  childData?: MemberWorkloadStatusRow
): boolean => {
  const typeFilterModel = api.getFilterModel()?.['body.type']

  if (!typeFilterModel || !typeFilterModel.values) {
    return true
  }

  const typeLabel = getEstimatedActualTypeLabel(childData?.estimatedActualType)

  return typeFilterModel.values.includes(typeLabel)
}

const buildSelectFilterModel = (
  values: string[],
  includeBlank: boolean,
  allOptionValues: string[]
): SelectFilter | null => {
  const isAllSelected =
    allOptionValues.length > 0 &&
    values.length === allOptionValues.length &&
    includeBlank
  return isAllSelected ? null : { values, includeBlank }
}

/**
 * プロジェクトフィルターの doesFilterPass を直接判定する関数
 */
const passesProjectFilter = (
  model: SelectFilter | null,
  getValue: (option: any) => string,
  valueGetter: (params: any) => any,
  context: any,
  node: IRowNode,
  data: unknown
): boolean => {
  if (!model) return true
  const selectValue = valueGetter({ node, data, context })
  if (
    model.includeBlank &&
    (!selectValue || (Array.isArray(selectValue) && selectValue.length === 0))
  ) {
    return true
  }
  if (
    Array.isArray(selectValue) &&
    selectValue.some((v: any) => model.values.includes(getValue(v)))
  ) {
    return true
  } else if (model.values.includes(getValue(selectValue))) {
    return true
  }
  return false
}

export const MemberProjectSelectFilter = ({
  model,
  onModelChange,
  api,
  getValue,
  getLabel,
  valueGetter,
  valueFormatter,
  sortValues,
  colDef,
  context,
  optionKey,
  getValuesForOptionFilter,
}: Props) => {
  // UI-local state
  const [text, setText] = useState('')
  const [options, setOptions] = useState<any[]>([])

  // Derive state from model (single source of truth)
  const allOptionValues = options.map(getValue)
  const selectModel = model as SelectFilter | null
  const values = selectModel?.values ?? allOptionValues
  const includeBlank = selectModel?.includeBlank ?? true
  const isAllSelected =
    !model || (values.length === allOptionValues.length && includeBlank)
  const isAllDeselected = model !== null && values.length === 0 && !includeBlank

  const getOptions = useCallback(async () => {
    if (getValuesForOptionFilter && optionKey && context[optionKey]) {
      const validUuids = await getValuesForOptionFilter(context)
      const contextOptions = context[optionKey] as any[]
      const filteredOptions = contextOptions.filter((opt: any) =>
        validUuids.includes(getValue(opt))
      )
      if (sortValues) {
        return sortValues(
          colDef.cellRendererParams?.uiMeta,
          filteredOptions,
          context
        )
      }
      return filteredOptions
    }

    const initialOptions: any[] = []
    api.forEachNode(node => {
      initialOptions.push(valueGetter({ node, context }))
    })
    const newOptionValues: string[] = []
    const newOptions: any[] = []
    initialOptions
      .filter(option => !!option)
      .forEach(option => {
        if (Array.isArray(option)) {
          option.forEach(o => {
            if (!newOptionValues.includes(getValue(o))) {
              newOptions.push(o)
              newOptionValues.push(getValue(o))
            }
          })
        } else if (!newOptionValues.includes(getValue(option))) {
          newOptions.push(option)
          newOptionValues.push(getValue(option))
        }
      })
    if (sortValues) {
      return sortValues(colDef.cellRendererParams?.uiMeta, newOptions, context)
    } else {
      return newOptions
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- api/context/valueGetter/getValue/sortValues/colDef は毎レンダーで新規参照になるため deps に入れない
  }, [])

  // Load options on mount if restoring from saved model
  // eslint-disable-next-line react-hooks/exhaustive-deps -- マウント時のみ実行
  // eslint-disable-next-line react-hooks/exhaustive-deps -- マウント時のみ実行。model 変更のたびにオプション再取得は不要（afterGuiAttached で取得する）
  useEffect(() => {
    if (model) {
      getOptions().then(setOptions)
    }
  }, [])

  useGridFilter({
    doesFilterPass(params: IDoesFilterPassParams) {
      const rowData = params.data as MemberWorkloadStatusRow | undefined

      if (isMemberRow(rowData)) {
        const children = getChildren(params.node)
        return children.some((child: IRowNode) => {
          const childData = child.data as MemberWorkloadStatusRow | undefined
          return (
            passesProjectFilter(
              selectModel,
              getValue,
              valueGetter,
              context,
              child,
              childData
            ) && passesTypeFilter(api, childData)
          )
        })
      }

      if (isTotalEstimatedActualRow(rowData)) {
        if (!passesTypeFilter(api, rowData)) {
          return false
        }

        if (!selectModel) {
          return true
        }

        const parent = params.node.parent
        if (!parent) return true
        return getChildren(parent).some((sibling: IRowNode) => {
          const siblingData = sibling.data as
            | MemberWorkloadStatusRow
            | undefined
          if (
            isTotalEstimatedActualRow(siblingData) ||
            isMemberRow(siblingData)
          ) {
            return false
          }
          return passesProjectFilter(
            selectModel,
            getValue,
            valueGetter,
            context,
            sibling,
            siblingData
          )
        })
      }

      return passesProjectFilter(
        selectModel,
        getValue,
        valueGetter,
        context,
        params.node,
        rowData
      )
    },

    afterGuiAttached() {
      getOptions().then(setOptions)
    },

    getModelAsString() {
      if (!model) return ''
      let labelList: string[] = []
      includeBlank && labelList.push('(Blank)')
      labelList = labelList.concat(
        options
          .filter(option => values.includes(getValue(option)))
          .map(option => getLabel(option))
      )
      return labelList.join(',')
    },
  })

  // Helpers for model updates
  const updateValues = (newValues: string[]) => {
    onModelChange(
      buildSelectFilterModel(newValues, includeBlank, allOptionValues)
    )
  }

  const updateIncludeBlank = (newIncludeBlank: boolean) => {
    onModelChange(
      buildSelectFilterModel(values, newIncludeBlank, allOptionValues)
    )
  }

  return (
    <BulkSheetFilter>
      <FilterInput
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder={'Search...'}
        sx={{ height: 22 }}
      />
      <SelectFilterArea>
        <SelectFilterFormControl
          label="(Select All)"
          control={
            <Checkbox
              checked={isAllSelected}
              indeterminate={!isAllSelected && !isAllDeselected}
              onChange={e =>
                e.target.checked
                  ? onModelChange(null)
                  : onModelChange({ values: [], includeBlank: false })
              }
              disableRipple={true}
            />
          }
        />
        <CheckboxArea>
          {!text && (
            <SelectFilterFormControl
              label="(Blanks)"
              control={
                <Checkbox
                  checked={includeBlank}
                  onChange={e => updateIncludeBlank(e.target.checked)}
                  disableRipple={true}
                />
              }
            />
          )}
          {options
            .filter(
              option =>
                !text ||
                (valueFormatter
                  ? valueFormatter({
                      colDef,
                      value: getLabel(option),
                    }).includes(text)
                  : getLabel(option).includes(text))
            )
            .map(option => {
              const optionValue = getValue(option)
              return (
                <SelectFilterFormControl
                  key={`select-filter-${optionValue}`}
                  label={
                    <FilterLabel
                      sx={{
                        backgroundColor: option.backgroundColor ?? 'inherit',
                      }}
                    >
                      {option.iconUrl && (
                        <img
                          src={option.iconUrl}
                          style={{
                            height: `${ICON_SIZE}px`,
                            width: `${ICON_SIZE}px`,
                            borderRadius: '50%',
                          }}
                        />
                      )}
                      <Typography sx={{ padding: '0 3px' }}>
                        {valueFormatter
                          ? valueFormatter({
                              colDef,
                              value: getLabel(option),
                            })
                          : getLabel(option)}
                      </Typography>
                    </FilterLabel>
                  }
                  control={
                    <Checkbox
                      checked={values.includes(optionValue)}
                      onChange={e => {
                        if (e.target.checked) {
                          updateValues([...values, optionValue])
                        } else {
                          updateValues(values.filter(v => v !== optionValue))
                        }
                      }}
                      disableRipple={true}
                    />
                  }
                />
              )
            })}
        </CheckboxArea>
      </SelectFilterArea>
      <FilterFooter loading={false} onClick={() => onModelChange(null)} />
    </BulkSheetFilter>
  )
}

export default MemberProjectSelectFilter
